
import { Collection } from "@/lib/api/types";
import { Card, CardContent } from "@/components/ui/card";
import { Box } from "lucide-react";
import { CollectionCardActions } from "./CollectionCardActions";
import { CollectionCardStats, useCollectionStats } from "../CollectionCardStats";
import { CollectionCardProgress } from "./CollectionCardProgress";
import { useCollectionCardProgress } from "@/hooks/useCollectionCardProgress";
import { cn } from "@/lib/utils";
import { CollectionInfo } from "../stats/CollectionInfo";
import { useState, useEffect } from "react";

interface CollectionCardProps {
  collection: Collection;
  isSelected: boolean;
  onNavigate: () => void;
  onEdit: (collection: Collection) => void;
  onDelete: (collection: Collection) => void;
}

export const CollectionCard = ({
  collection,
  isSelected,
  onNavigate,
  onEdit,
  onDelete,
}: CollectionCardProps) => {
  const [forceShowProgress, setForceShowProgress] = useState(false);
  const { data: collectionStats } = useCollectionStats([collection]);
  const stats = collectionStats?.find(
    (stat) => stat.collection_id === collection.id
  );

  const {
    isProcessing,
    progressPercentage,
    processedItems,
    totalItems,
    currentAction,
    processType
  } = useCollectionCardProgress(collection.id);

  // Show progress if actively processing OR if we forced it via regeneration
  const shouldShowProgress = isProcessing || forceShowProgress;

  // Auto-hide forced progress after processing completes
  useEffect(() => {
    if (forceShowProgress && !isProcessing && progressPercentage >= 100) {
      const timeout = setTimeout(() => {
        setForceShowProgress(false);
      }, 3000); // Give 3 seconds to see completion
      
      return () => clearTimeout(timeout);
    }
  }, [forceShowProgress, isProcessing, progressPercentage]);

  const handleClick = () => {
    console.log('Card clicked, calling onNavigate for collection:', collection.id);
    onNavigate();
  };

  const handleRegenerateStart = () => {
    // Force progress display when regeneration starts
    setForceShowProgress(true);
    
    // Auto-hide the forced progress after 30 seconds if no real progress is detected
    setTimeout(() => {
      setForceShowProgress(false);
    }, 30000);
  };

  return (
    <Card
      className={cn(
        "group relative transition-all duration-200 cursor-pointer overflow-hidden max-w-[500px] w-full h-full",
        "bg-gradient-to-br from-[hsl(var(--gradient-start))] via-[hsl(var(--gradient-mid))] to-[hsl(var(--gradient-end))]",
        isSelected
          ? "ring-2 ring-primary shadow-lg"
          : "hover:ring-1 hover:ring-primary/50"
      )}
      onClick={handleClick}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <CardContent className="p-6 flex flex-col relative h-full">
        <div className="flex items-start justify-between mb-4">
          <Box className="h-8 w-8 text-primary/80 group-hover:text-primary transition-colors" />
          <CollectionCardActions
            collection={collection}
            onEdit={onEdit}
            onDelete={onDelete}
            onRegenerateStart={handleRegenerateStart}
          />
        </div>
        
        {/* Collection name and creation date at the top */}
        <div className="mb-4">
          <CollectionInfo collection={collection} />
        </div>
        
        {/* This div will grow to push content to bottom */}
        <div className="flex-grow"></div>
        
        {/* Progress indicator when processing */}
        <CollectionCardProgress
          isProcessing={shouldShowProgress}
          progressPercentage={progressPercentage}
          processedItems={processedItems}
          totalItems={totalItems}
          currentAction={currentAction}
          processType={processType}
        />
        
        {/* Bottom section with stats - always at bottom */}
        <div className="mt-auto">
          <CollectionCardStats 
            collection={collection} 
            stats={stats}
            collectionId={collection.id}
          />
        </div>
      </CardContent>
    </Card>
  );
};
